import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { ScreenHeader } from '../../components/layout/ScreenHeader';
import { FileText, Filter, Download, Calendar, Tag, Banknote, User, X, Edit, Trash2 } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { API_BASE_URL } from '../../config';

type ExpenseCategory =
  | 'مصاريف المشاريع'
  | 'مصاريف الإدارة وأقسامها'
  | 'مصاريف الفروع'
  | 'مصاريف الخدمات اللوجستية'
  | 'مصاريف حركة الشركة'
  | 'مصاريف الخدمات'
  | 'مصاريف المستودعات'
  | 'مصاريف متنوعة'
  | 'مصاريف قسم الاتصال';

const CATEGORIES: ExpenseCategory[] = [
  'مصاريف المشاريع',
  'مصاريف الإدارة وأقسامها',
  'مصاريف الفروع',
  'مصاريف الخدمات اللوجستية',
  'مصاريف حركة الشركة',
  'مصاريف الخدمات',
  'مصاريف المستودعات',
  'مصاريف متنوعة',
  'مصاريف قسم الاتصال',
];

const CATEGORY_STYLES: Record<ExpenseCategory, { chipSelected: string; badge: string }> = {
  'مصاريف المشاريع': {
    chipSelected: 'bg-blue-50 text-blue-700 border-blue-200',
    badge: 'bg-blue-50 text-blue-700',
  },
  'مصاريف الإدارة وأقسامها': {
    chipSelected: 'bg-purple-50 text-purple-700 border-purple-200',
    badge: 'bg-purple-50 text-purple-700',
  },
  'مصاريف الفروع': {
    chipSelected: 'bg-teal-50 text-teal-700 border-teal-200',
    badge: 'bg-teal-50 text-teal-700',
  },
  'مصاريف الخدمات اللوجستية': {
    chipSelected: 'bg-orange-50 text-orange-700 border-orange-200',
    badge: 'bg-orange-50 text-orange-700',
  },
  'مصاريف حركة الشركة': {
    chipSelected: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    badge: 'bg-indigo-50 text-indigo-700',
  },
  'مصاريف الخدمات': {
    chipSelected: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    badge: 'bg-cyan-50 text-cyan-700',
  },
  'مصاريف المستودعات': {
    chipSelected: 'bg-amber-50 text-amber-700 border-amber-200',
    badge: 'bg-amber-50 text-amber-700',
  },
  'مصاريف متنوعة': {
    chipSelected: 'bg-slate-100 text-slate-700 border-slate-200',
    badge: 'bg-slate-100 text-slate-700',
  },
  'مصاريف قسم الاتصال': {
    chipSelected: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    badge: 'bg-fuchsia-50 text-fuchsia-700',
  },
};

type ExpenseLine = {
  id: string;
  date?: string; // YYYY-MM-DD
  company?: string;
  invoiceNumber?: string;
  description?: string;
  reason: ExpenseCategory; // required
  amount: number; // >= 0
  bankFees?: number; // optional
  buyerName?: string;
  notes?: string;
};

type ExpenseSession = {
  key: string;
  sessionNumber?: string;
  sessionAmount: number;
  lines: ExpenseLine[];
  createdAt: string;
  updatedAt?: string;
};

const buildSessionKey = (ts?: number) => `expense-session-${ts ?? Date.now()}`;

const escapeCSV = (val: unknown) => {
  const s = String(val ?? '');
  const needsQuotes = /[",\n]/.test(s);
  const inner = s.replace(/"/g, '""');
  return needsQuotes ? `"${inner}"` : inner;
};

const InstantExpensesScreen: React.FC = () => {
  const { user } = useAppContext();
  // Helper to build auth headers robustly. Backend accepts either numeric user id or username in X-User-Id.
  // We always send X-User-Role as well. If role is Admin, backend allows missing X-User-Id.
  const getAuthHeaders = () => {
    const headers: Record<string, string> = {};
    const role = String((user as any)?.role ?? '').trim();
    if (role) headers['X-User-Role'] = role;
    // Prefer numeric id; otherwise fallback to username; as a last resort, employeeId.
    const candidates = [
      (user as any)?.id,
      (user as any)?.username,
      (user as any)?.employeeId,
    ].filter(v => v !== undefined && v !== null && String(v).trim() !== '');
    const chosen = candidates.length > 0 ? String(candidates[0]) : '';
    if (chosen) headers['X-User-Id'] = chosen;
    return headers;
  };
  const [apiOnline, setApiOnline] = useState<boolean | null>(null);
  const [apiMessage, setApiMessage] = useState<string>('');
  const [sessionKey, setSessionKey] = useState<string>(() => buildSessionKey());
  const [sessionNumber, setSessionNumber] = useState<string>('');
  const [sessionAmount, setSessionAmount] = useState<string>('');
  const [lines, setLines] = useState<ExpenseLine[]>([]);
  const [filterCat, setFilterCat] = useState<ExpenseCategory | 'ALL'>('ALL');
  const [sessions, setSessions] = useState<{ key: string; label: string }[]>([]);
  // العهدة المفتوحة حالياً (عند الاختيار من القائمة)
  const [activeSheetId, setActiveSheetId] = useState<string | null>(null);
  const [lineAddError, setLineAddError] = useState<string | null>(null);

  // تحميل العهد السابقة (من الباكند)
  const [isBrowseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);
  const [sheets, setSheets] = useState<Array<{ id: string; custody_number: string; custody_amount: number; status: string; last_modified?: string }>>([]);
  // Quick pick modal to choose a sheet quickly
  const [isQuickPickOpen, setQuickPickOpen] = useState(false);
  const [quickPickQuery, setQuickPickQuery] = useState('');
  const [quickPickManualId, setQuickPickManualId] = useState('');

  const [draft, setDraft] = useState<Partial<ExpenseLine>>({});
  const notesRef = useRef<HTMLInputElement | null>(null);
  // حفظ العهدة في الباكند
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [savedSheetId, setSavedSheetId] = useState<string | null>(null);
  const [savedSheetNumber, setSavedSheetNumber] = useState<string | null>(null);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);

  // Load last incomplete session if exists
  useEffect(() => {
    try {
      const allKeys = Object.keys(localStorage).filter(k => k.startsWith('expense-session-'));
      const latest = allKeys.sort().reverse()[0];
      if (latest) {
        const raw = localStorage.getItem(latest);
        if (raw) {
          const parsed = JSON.parse(raw) as ExpenseSession;
          setSessionKey(parsed.key);
          setSessionNumber(parsed.sessionNumber || '');
          setSessionAmount(String(parsed.sessionAmount ?? ''));
          setLines(Array.isArray(parsed.lines) ? parsed.lines : []);
        }
      }
      // Populate session list
      refreshSessions();
    } catch {}
  }, []);

  // Check API connectivity (health) periodically
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/health`);
        const ok = res.ok;
        const body = await res.json().catch(() => ({}));
        if (!cancelled) {
          setApiOnline(ok);
          setApiMessage(ok ? 'متصل بالخادم' : (body?.message ? String(body.message) : `تعذر الاتصال بالخادم (HTTP ${res.status})`));
        }
      } catch (err: any) {
        if (!cancelled) {
          setApiOnline(false);
          setApiMessage('تعذر الاتصال بالخادم (تحقق من الإنترنت أو صلاحيات CORS).');
        }
      }
    };
    check();
    const id = setInterval(check, 15000); // every 15s
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const totalSpent = useMemo(() => {
    return lines.reduce((sum, l) => sum + (Number(l.amount || 0) + Number(l.bankFees || 0)), 0);
  }, [lines]);

  const remaining = useMemo(() => {
    const amt = Number(sessionAmount || 0);
    return amt - totalSpent;
  }, [sessionAmount, totalSpent]);

  const persist = (nextLines?: ExpenseLine[], nextAmount?: string, nextNumber?: string) => {
    const payload: ExpenseSession = {
      key: sessionKey,
      sessionNumber: nextNumber ?? sessionNumber,
      sessionAmount: Number((nextAmount ?? sessionAmount) || 0),
      lines: nextLines ?? lines,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(sessionKey, JSON.stringify(payload));
    refreshSessions();
  };

  useEffect(() => {
    persist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionNumber, sessionAmount, lines]);

  // السماح بإضافة البنود حتى لو لم يتم إدخال مبلغ العهدة بعد
  // سابقاً كنا نمنع الإضافة إذا كان مبلغ العهدة <= 0، وهذا عطّل زر إضافة السطر وحدث الإدخال بالإنتر
  // الآن نسمح بالإضافة دائماً، وسنعالج تجاوز المبلغ لاحقاً عند الحفظ/التحقق.
  const canAddLines = Number(sessionAmount || 0) >= 0;

  const saveDraftLine = () => {
    if (!canAddLines) return false;
    setLineAddError(null);
    const reason = draft.reason as ExpenseCategory | undefined;
    const amount = Number(draft.amount ?? 0);
    const dateReq = (draft.date ?? '').toString().trim();
    const companyReq = (draft.company ?? '').toString().trim();
    const descReq = (draft.description ?? '').toString().trim();
    if (!dateReq || !companyReq || !descReq || !reason || isNaN(amount) || amount <= 0) {
      setLineAddError('يرجى تعبئة الحقول الإلزامية: التاريخ، الشركة، البيان، سبب الصرف، والمبلغ (> 0).');
      return false;
    }
    const bankFees = Number(draft.bankFees ?? 0) || 0;

    // إذا كانت هناك عهدة محددة، أضف السطر إلى تلك العهدة في الباكند مباشرة
    if (activeSheetId) {
      // أرسل السطر إلى الخادم
      (async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets/${activeSheetId}/lines`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({
              date: dateReq,
              company: companyReq,
              invoiceNumber: draft.invoiceNumber || null,
              description: descReq,
              reason,
              amount,
              bankFees: bankFees || null,
              buyerName: draft.buyerName || null,
              notes: draft.notes || null,
            }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const added: ExpenseLine = {
            id: String(data.id),
            date: data.date || dateReq,
            company: data.company || companyReq,
            invoiceNumber: data.invoiceNumber || undefined,
            description: data.description || descReq,
            reason: (data.reason as ExpenseCategory) ?? reason,
            amount: Number(data.amount || amount),
            bankFees: typeof data.bankFees === 'number' ? data.bankFees : (bankFees || undefined),
            buyerName: data.buyerName || undefined,
            notes: data.notes || undefined,
          };
          const next = [...lines, added];
          setLines(next);
          setDraft({});
          persist(next);
        } catch (err: any) {
          console.error('Failed to add line to backend:', err);
          setLineAddError('تعذر إضافة البند إلى العهدة على الخادم. حاول مرة أخرى.');
        }
      })();
      return true;
    }

    // إن لم تكن هناك عهدة محددة، أضف محلياً مع تنبيه للمستخدم بحفظ/اختيار العهدة
    const newLine: ExpenseLine = {
      id: crypto.randomUUID(),
      date: dateReq,
      company: companyReq,
      invoiceNumber: draft.invoiceNumber || undefined,
      description: descReq,
      reason,
      amount,
      bankFees: bankFees || undefined,
      buyerName: draft.buyerName || undefined,
      notes: draft.notes || undefined,
    };
    const next = [...lines, newLine];
    setLines(next);
    setDraft({});
    persist(next);
    setLineAddError('تمت إضافة البند محلياً. يرجى حفظ العهدة أو اختيارها من الأعلى لربط البنود بها.');
    return true;
  };

  // إدخال السطر بالإنتر من أي حقل في صف المسودة
  const handleDraftEnter: React.KeyboardEventHandler<any> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveDraftLine();
    }
  };

  const filteredLines = useMemo(() => {
    if (filterCat === 'ALL') return lines;
    return lines.filter(l => l.reason === filterCat);
  }, [lines, filterCat]);

  const exportCSV = () => {
    const headerA = ['رقم/اسم العهدة', 'مبلغ العهدة', 'الإجمالي المنصرف', 'الباقي'];
    const sessionA = [sessionNumber || '', String(Number(sessionAmount || 0)), String(totalSpent), String(remaining)];
    const cols = ['التاريخ', 'الشركة', 'رقم الفاتورة', 'البيان', 'سبب الصرف', 'المبلغ', 'المصروفات البنكية', 'الإجمالي', 'اسم المشتري', 'ملاحظات'];
    const rows = filteredLines.map(l => [
      l.date || '',
      l.company || '',
      l.invoiceNumber || '',
      l.description || '',
      l.reason,
      String(l.amount ?? 0),
      String(l.bankFees ?? 0),
      String((Number(l.amount || 0) + Number(l.bankFees || 0))),
      l.buyerName || '',
      l.notes || '',
    ]);
    const csv = [
      headerA.map(escapeCSV).join(','),
      sessionA.map(escapeCSV).join(','),
      cols.map(escapeCSV).join(','),
      ...rows.map(r => r.map(escapeCSV).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `instant-expenses-${sessionNumber || 'session'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // إنشاء/حفظ العهدة في الباكند
  const saveCustodyToBackend = async () => {
    if (!user) { setSaveStatus('error'); setBrowseError('الرجاء تسجيل الدخول'); return; }
    const number = (sessionNumber || '').trim();
    const amountNum = Number(sessionAmount || 0);
    if (!number) {
      setBrowseError('يرجى إدخال رقم/اسم العهدة قبل الحفظ.');
      setSaveStatus('error');
      return;
    }
    setSaveStatus('saving');
    try {
      // تحقق أولاً من وجود العهدة بهذا الرقم
      try {
        const checkRes = await fetch(`${API_BASE_URL}/instant-expenses/sheets/by-number/${encodeURIComponent(number)}`, { headers: { ...getAuthHeaders() } });
        if (checkRes.ok) {
          const data = await checkRes.json();
          const sheet = data && data.sheet ? data.sheet : data;
          setSavedSheetId(String(sheet.id));
          setSavedSheetNumber(String(sheet.custodyNumber || number));
          setSessionAmount(String(Number(sheet.custodyAmount || 0)));
          setSaveStatus('saved');
          await openSheet(number);
          return;
        }
      } catch {}
      const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          // Server middleware also checks req.body.employeeId if header is missing, so include robust fallback
          employeeId: (user as any).employeeId || (user as any).id || (user as any).username,
          custodyNumber: number,
          custodyAmount: amountNum,
          notes: null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // تحديث حالة الحفظ وإضافة النتيجة إلى الكاش لظهور فوري في واجهة الاختيار
      const mapped = {
        id: String(data.id),
        custody_number: String(data.custodyNumber ?? number),
        custody_amount: Number(data.custodyAmount ?? amountNum),
        status: String(data.status ?? 'OPEN'),
        last_modified: data.lastModified ? String(data.lastModified) : new Date().toISOString(),
      };
      setSavedSheetId(mapped.id);
      // خزّن رقم العهدة فقط (إن لم يتوفر من الخادم، نستخرج الرقم من المعرف بإزالة البادئة CUST-)
      setSavedSheetNumber(mapped.custody_number ? String(mapped.custody_number) : String(mapped.id).replace(/^CUST-/, ''));
      setSaveStatus('saved');
      // أضف/حدّث في الحالة الحالية أيضاً ليظهر فوراً في "اختر عهدة"
      setSheets(prev => {
        const idx = prev.findIndex(x => String(x.id) === mapped.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = mapped;
          return next;
        }
        return [mapped, ...prev];
      });
      try {
        const raw = localStorage.getItem('instant-expenses-sheets-cache');
        const parsed = raw ? JSON.parse(raw) : { at: Date.now(), items: [] };
        const items = Array.isArray(parsed.items) ? parsed.items : [];
        const existsIdx = items.findIndex((x: any) => String(x.id) === mapped.id);
        if (existsIdx >= 0) items[existsIdx] = mapped; else items.unshift(mapped);
        localStorage.setItem('instant-expenses-sheets-cache', JSON.stringify({ at: Date.now(), items }));
      } catch {}
      // افتح العهدة تلقائياً بعد الحفظ لبدء إضافة السطور مباشرة
      try {
        const numForOpen = String(mapped.custody_number || String(mapped.id).replace(/^CUST-/, ''));
        await openSheet(numForOpen);
      } catch {}
    } catch (err) {
      console.error('Failed to save custody sheet:', err);
      setSaveStatus('error');
      setBrowseError('تعذر حفظ العهدة على الخادم. تأكد من اتصال قاعدة البيانات.');
    }
  };

  const refreshSessions = () => {
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith('expense-session-')).sort().reverse();
      const metas = keys.map(k => {
        try {
          const raw = localStorage.getItem(k);
          const p = raw ? (JSON.parse(raw) as ExpenseSession) : null;
          const created = p?.createdAt ? new Date(p.createdAt) : null;
          const label = `${p?.sessionNumber || 'جلسة'}${created ? ` • ${created.toLocaleDateString('ar-EG')} ${created.toLocaleTimeString('ar-EG')}` : ''}`;
          return { key: k, label };
        } catch {
          return { key: k, label: k };
        }
      });
      setSessions(metas);
    } catch {}
  };

  const loadSession = (k: string) => {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) return;
      const p = JSON.parse(raw) as ExpenseSession;
      setSessionKey(p.key);
      setSessionNumber(p.sessionNumber || '');
      setSessionAmount(String(p.sessionAmount ?? ''));
      setLines(Array.isArray(p.lines) ? p.lines : []);
    } catch {}
  };

  const createNewSession = () => {
    const newKey = buildSessionKey();
    setSessionKey(newKey);
    setSessionNumber('');
    setSessionAmount('');
    setLines([]);
    // Persist empty session scaffold
    localStorage.setItem(newKey, JSON.stringify({
      key: newKey,
      sessionNumber: '',
      sessionAmount: 0,
      lines: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as ExpenseSession));
    refreshSessions();
  };

  // Fetch custody sheets from backend
  const fetchSheets = async () => {
    if (!user) { setBrowseError('الرجاء تسجيل الدخول'); return; }
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets?ts=${Date.now()}`, {
        headers: { ...getAuthHeaders() }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Expect array of { id, custodyNumber, custodyAmount, status, lastModified }
      const mapped = (Array.isArray(data) ? data : []).map((s: any) => ({
        id: String(s.id),
        custody_number: String(s.custodyNumber ?? ''),
        custody_amount: Number(s.custodyAmount ?? 0),
        status: String(s.status ?? 'OPEN'),
        last_modified: s.lastModified ? String(s.lastModified) : undefined,
      }));
      // sort newest first by last_modified
      mapped.sort((a, b) => new Date(b.last_modified || 0).getTime() - new Date(a.last_modified || 0).getTime());
      setSheets(mapped);
      // cache for fallback
      try { localStorage.setItem('instant-expenses-sheets-cache', JSON.stringify({ at: Date.now(), items: mapped })); } catch {}
    } catch (e: any) {
      // في حال فشل التحميل: نحافظ على القائمة الحالية إن وُجدت، ونحاول القراءة من الكاش بدون إفراغها.
      try {
        const raw = localStorage.getItem('instant-expenses-sheets-cache');
        if (raw) {
          const parsed = JSON.parse(raw);
          const items = Array.isArray(parsed?.items) ? parsed.items : [];
          // لا نفرغ القائمة إن كانت لدينا عناصر بالفعل؛ إن كانت فارغة فقط نضع الكاش
          setSheets(prev => (prev && prev.length > 0 ? prev : items));
        }
        // إذا لا يوجد كاش، نبقي الحالة كما هي بدون إفراغ
      } catch { /* تجاهل أخطاء الكاش */ }
      // أظهر رسالة خطأ للمستخدم بدلاً من إخفائها
      const msg = String(e?.message || '').includes('HTTP 401')
        ? 'غير مصرح بتحميل العهد. تحقق من هوية المستخدم والصلاحيات.'
        : String(e?.message || '').includes('HTTP 403')
        ? 'تم رفض الطلب من الخادم (403).'
        : String(e?.message || '').includes('HTTP 404')
        ? 'لم يتم العثور على بيانات للعهد.'
        : 'تعذر تحميل العهد السابقة. حاول مرة أخرى لاحقًا.';
      setBrowseError(msg);
    } finally {
      setBrowseLoading(false);
    }
  };

  // قم بتحميل العهد تلقائياً عند توفر المستخدم
  useEffect(() => {
    if (user) {
      fetchSheets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const openSheet = async (sheetIdOrNumber: string) => {
    if (!user) return;
    // حدّد نوع القيمة المُمرّرة: رقم فقط أم معرّف مسبوق بـ CUST-
    const looksNumeric = /^\d+$/.test(sheetIdOrNumber);
    const hasCustPrefix = /^CUST-/.test(sheetIdOrNumber);
    const numericCandidate = hasCustPrefix ? sheetIdOrNumber.replace(/^CUST-/, '') : sheetIdOrNumber;
    const tryLoadById = async (id: string) => {
      const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets/${id}`, { headers: { ...getAuthHeaders() } });
      return res.ok ? res.json() : Promise.reject({ status: res.status });
    };
    const tryLoadByNumber = async (num: string) => {
      const res = await fetch(`${API_BASE_URL}/instant-expenses/sheets/by-number/${encodeURIComponent(num)}`, { headers: { ...getAuthHeaders() } });
      return res.ok ? res.json() : Promise.reject({ status: res.status });
    };
    const tryFindFromList = async (numOrId: string) => {
      const listRes = await fetch(`${API_BASE_URL}/instant-expenses/sheets?ts=${Date.now()}`, { headers: { ...getAuthHeaders() } });
      if (!listRes.ok) throw { status: listRes.status };
      const arr = await listRes.json();
      const items = Array.isArray(arr) ? arr : [];
      // ابحث أولاً بالرقم ثم بالمعرّف
      const matchByNum = items.find((s: any) => String(s.custodyNumber ?? '').toLowerCase() === String(numOrId).toLowerCase());
      if (matchByNum && matchByNum.id) return await tryLoadById(String(matchByNum.id));
      const matchById = items.find((s: any) => String(s.id ?? '').toLowerCase() === String(numOrId).toLowerCase());
      if (matchById && matchById.id) return await tryLoadById(String(matchById.id));
      throw { status: 404 };
    };
    const normalizeAndApply = (data: any, overrideId?: string) => {
      const sheet = data && data.sheet ? data.sheet : data;
      const linesArr = data && data.lines ? data.lines : sheet?.lines || [];
      setSessionNumber(String(sheet?.custodyNumber ?? ''));
      setSessionAmount(String(Number(sheet?.custodyAmount ?? 0)));
      const nextLines: ExpenseLine[] = Array.isArray(linesArr) ? linesArr.map((l: any) => ({
        id: String(l.id),
        date: l.date || undefined,
        company: l.company || undefined,
        invoiceNumber: l.invoiceNumber || undefined,
        description: l.description || undefined,
        reason: (l.reason as ExpenseCategory) ?? 'مصاريف متنوعة',
        amount: Number(l.amount ?? 0),
        bankFees: typeof l.bankFees === 'number' ? l.bankFees : undefined,
        buyerName: l.buyerName || undefined,
        notes: l.notes || undefined,
      })) : [];
      setLines(nextLines);
      const finalId = String(sheet?.id ?? overrideId ?? sheetIdOrNumber);
      setActiveSheetId(finalId);
      setSessionKey(`expense-session-${finalId}`);
      setQuickPickOpen(false);
    };
    try {
      if (looksNumeric) {
        // إذا كانت القيمة رقمية، جرب الفتح بالرقم مباشرة
        try {
          const dataByNum = await tryLoadByNumber(numericCandidate);
          normalizeAndApply(dataByNum);
          return;
        } catch (err: any) {
          if (err?.status !== 404) throw new Error(`HTTP ${err?.status ?? 'ERR'}`);
          // ابحث من القائمة كحل احتياطي
          const dataFromList = await tryFindFromList(numericCandidate);
          normalizeAndApply(dataFromList);
          return;
        }
      }
      if (hasCustPrefix) {
        // إذا كانت القيمة معرّف CUST-، جرب أولاً بالمعرّف، وإن فشل حاول بالرقم المستخرج
        try {
          const dataById = await tryLoadById(sheetIdOrNumber);
          normalizeAndApply(dataById);
          return;
        } catch (errId: any) {
          if (errId?.status !== 404) throw new Error(`HTTP ${errId?.status ?? 'ERR'}`);
          try {
            const dataByNum = await tryLoadByNumber(numericCandidate);
            normalizeAndApply(dataByNum);
            return;
          } catch (errNum: any) {
            if (errNum?.status !== 404) throw new Error(`HTTP ${errNum?.status ?? 'ERR'}`);
            const dataFromList = await tryFindFromList(numericCandidate);
            normalizeAndApply(dataFromList, `CUST-${numericCandidate}`);
            return;
          }
        }
      }
      // الحالة العامة: جرب بالمعرّف ثم ابحث من القائمة
      try {
        const dataById = await tryLoadById(sheetIdOrNumber);
        normalizeAndApply(dataById);
        return;
      } catch (errGen: any) {
        if (errGen?.status !== 404) throw new Error(`HTTP ${errGen?.status ?? 'ERR'}`);
        const dataFromList = await tryFindFromList(sheetIdOrNumber);
        normalizeAndApply(dataFromList);
        return;
      }
    } catch (e: any) {
      if (String(e?.message || '').includes('HTTP 404')) {
        setBrowseError('العهدة غير موجودة على هذا الخادم أو لم تُنشأ هنا.');
      } else if (String(e?.message || '').includes('HTTP 401')) {
        setBrowseError('غير مصرح لك بفتح هذه العهدة (يرجى التأكد من المستخدم والصلاحيات).');
      } else {
        setBrowseError('تعذر فتح العهدة المحددة.');
      }
    }
  };

  return (
    <div className="space-y-6">
      <ScreenHeader
        icon={FileText}
        title={'المصروفات اللحظية'}
        colorClass="bg-nav-accounting"
        onBack="/purchase-archive"
      />

      {/* اتصال الخادم + معلومات المستخدم */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`px-2 py-1 rounded border ${apiOnline === false ? 'bg-red-50 text-red-700 border-red-200' : apiOnline === true ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
          {apiOnline === null ? 'التحقق من اتصال الخادم...' : apiMessage}
        </span>
        <span className="px-2 py-1 rounded border bg-slate-50 text-slate-600 border-slate-200">
          API: {API_BASE_URL}
        </span>
        {user && (
          <span className="px-2 py-1 rounded border bg-slate-50 text-slate-600 border-slate-200">
            المستخدم: {String(user.id)} • الدور: {String((user as any).role)}
          </span>
        )}
      </div>

      {/* Quick pick button */}
      <div className="flex justify-between -mt-4">
        <div className="flex items-center gap-2 text-xs">
          {saveStatus === 'saved' && (
            <span className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
              تم حفظ العهدة بنجاح{savedSheetNumber ? ` — رقم العهدة: ${savedSheetNumber}` : ''}
              {savedSheetNumber && (
                <Button size="xs" variant="secondary" onClick={() => openSheet(savedSheetNumber!)}>
                  فتح الآن
                </Button>
              )}
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">تعذر حفظ العهدة</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="primary" onClick={saveCustodyToBackend} disabled={saveStatus === 'saving'}>
            <Banknote size={16} className="ml-2" />
            حفظ العهدة
          </Button>
        </div>
      </div>

      {/* Always-visible Sheets List (بدون نافذة) */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <CardTitle className="text-base">العهد السابقة</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => fetchSheets()}>
              <Tag size={16} className="ml-2" />
              تحديث العهد
            </Button>
          </div>
        </CardHeader>
      <CardContent className="space-y-3">
          {isBrowseLoading && (
            <div className="p-2 text-xs text-slate-600 dark:text-slate-300">جاري تحميل العهد...</div>
          )}
          {browseError && (
            <div className="p-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded flex items-center justify-between gap-2">
              <span>{browseError}</span>
              <Button size="xs" variant="outline" onClick={() => fetchSheets()}>إعادة المحاولة</Button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-600 dark:text-slate-300">ابحث بالاسم/الرقم</label>
              <Input value={quickPickQuery} onChange={e => setQuickPickQuery(e.target.value)} placeholder="مثال: عهدة مشروع الرياض" />
            </div>
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300">أدخل رقم العهدة</label>
              <div className="flex gap-2">
                <Input value={quickPickManualId} onChange={e => setQuickPickManualId(e.target.value)} placeholder="رقم" />
                <Button size="sm" onClick={() => { if (quickPickManualId.trim()) { openSheet(quickPickManualId.trim()); } }}>فتح</Button>
              </div>
            </div>
          </div>

          <div className="max-h-64 overflow-auto border rounded">
            {sheets.length === 0 ? (
              <div className="p-3 text-xs text-slate-500">لا يوجد عهد سابقة</div>
            ) : (
              sheets
                .filter(s => (quickPickQuery ? (s.custody_number?.toLowerCase().includes(quickPickQuery.toLowerCase())) : true))
                .map(s => (
                  <div key={s.id} className="grid grid-cols-5 gap-2 py-1 px-2 text-xs items-center hover:bg-slate-50 dark:hover:bg-slate-800">
                    <div className="col-span-2">{s.custody_number || '-'}</div>
                    <div>{Number(s.custody_amount || 0).toLocaleString()}</div>
                    <div><Badge className={`px-2 py-0.5 rounded-full text-[11px] ${s.status === 'CLOSED' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'}`}>{s.status === 'CLOSED' ? 'مغلقة' : 'مفتوحة'}</Badge></div>
                    <div className="text-right"><Button size="xs" onClick={() => { const num = String(s.custody_number ?? String(s.id ?? '').replace(/^CUST-/, '')); openSheet(num); }}>اختيار</Button></div>
                  </div>
                ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Session Header */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">بيانات العهدة</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* إدارة الجلسات غير لازمة حسب التوجيه، تمت إزالتها */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            {activeSheetId && (
              <div className="md:col-span-4">
                <div className="text-[12px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-3 py-2">
                  أنت تعمل الآن على العهدة: <strong className="font-semibold">{sessionNumber || 'غير معروفة'}</strong>
                </div>
              </div>
            )}
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300">رقم/اسم العهدة</label>
              <Input
                value={sessionNumber}
                onChange={e => setSessionNumber(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    saveCustodyToBackend();
                  }
                }}
                placeholder="مثال: عهدة مشروع الرياض"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300">مبلغ العهدة (ريال)</label>
              <Input
                type="number"
                min={0}
                value={sessionAmount}
                onChange={e => setSessionAmount(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    saveCustodyToBackend();
                  }
                }}
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300">الإجمالي المنصرف</label>
              <div className="px-3 py-2 rounded border bg-slate-50 dark:bg-slate-800 text-sm">{totalSpent.toLocaleString()}</div>
            </div>
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300">المتبقي</label>
              <div className={`px-3 py-2 rounded border text-sm ${remaining < 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-50 dark:bg-slate-800'}`}>{remaining.toLocaleString()}</div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex gap-1 items-center text-[11px] text-slate-500">
              <Filter size={14} />
              <span>تصفية حسب سبب الصرف:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              <Button
                size="xs"
                variant="ghost"
                className={`rounded-full ${filterCat === 'ALL' ? 'bg-sky-50 text-sky-700 border border-sky-200' : 'text-slate-600 border border-slate-200 hover:bg-slate-100'}`}
                onClick={() => setFilterCat('ALL')}
              >
                الكل
              </Button>
              {CATEGORIES.map(cat => (
                <Button
                  key={cat}
                  size="xs"
                  variant="ghost"
                  className={`rounded-full ${filterCat === cat ? CATEGORY_STYLES[cat].chipSelected + ' border' : 'text-slate-600 border border-slate-200 hover:bg-slate-100'}`}
                  onClick={() => setFilterCat(cat)}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">بنود المصروفات</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* رسالة تنبيهية اختيارية عند عدم إدخال مبلغ العهدة */}
          {Number(sessionAmount || 0) === 0 && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              ملاحظة: لم يتم إدخال مبلغ العهدة بعد. يمكنك إضافة البنود وسيتم التحقق من الإجمالي لاحقاً.
            </div>
          )}

          {/* existing lines */}
          <div className="space-y-2">
            {filteredLines.length === 0 ? (
              <div className="text-sm text-slate-500">لا توجد بنود بعد.</div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[1000px] grid grid-cols-11 gap-2 text-xs font-semibold text-slate-600">
                  <div className="px-2">التاريخ</div>
                  <div className="px-2">الشركة</div>
                  <div className="px-2">رقم الفاتورة</div>
                  <div className="px-2">البيان</div>
                    <div className="px-2">سبب الصرف</div>
                  <div className="px-2">المبلغ</div>
                  <div className="px-2">المصروفات البنكية</div>
                  <div className="px-2">الإجمالي</div>
                  <div className="px-2">اسم المشتري</div>
                  <div className="px-2">ملاحظات</div>
                  <div className="px-2 text-right">إجراءات</div>
                </div>
                {filteredLines.map((l) => (
                  <div key={l.id} className="min-w-[1000px] grid grid-cols-11 gap-2 py-1 text-xs">
                    <div className="px-2">{l.date || '-'}</div>
                    <div className="px-2">{l.company || '-'}</div>
                    <div className="px-2">{l.invoiceNumber || '-'}</div>
                    <div className="px-2">{l.description || '-'}</div>
                    <div className="px-2">
                      <Badge className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full ${CATEGORY_STYLES[l.reason].badge}`}>
                        {l.reason}
                      </Badge>
                    </div>
                    <div className="px-2">{Number(l.amount || 0).toLocaleString()}</div>
                    <div className="px-2">{Number(l.bankFees || 0).toLocaleString()}</div>
                    <div className="px-2">{(Number(l.amount || 0) + Number(l.bankFees || 0)).toLocaleString()}</div>
                    <div className="px-2">{l.buyerName || '-'}</div>
                    <div className="px-2">{l.notes || '-'}</div>
                    <div className="px-2 flex gap-1 justify-end">
                      <Button size="xs" variant="outline" onClick={() => startEditLine(l.id)} title="تعديل"><Edit size={14} /></Button>
                      <Button size="xs" variant="destructive" onClick={() => deleteLine(l.id)} title="حذف"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* draft row (editable) */}
          <div className="overflow-x-auto">
            <div className="min-w-[900px] grid grid-cols-10 gap-2">
              <Input type="date" value={draft.date ?? ''} onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} onKeyDown={handleDraftEnter} />
              <Input placeholder="الشركة" value={draft.company ?? ''} onChange={e => setDraft(d => ({ ...d, company: e.target.value }))} onKeyDown={handleDraftEnter} />
              <Input placeholder="رقم الفاتورة" value={draft.invoiceNumber ?? ''} onChange={e => setDraft(d => ({ ...d, invoiceNumber: e.target.value }))} onKeyDown={handleDraftEnter} />
              <Input placeholder="البيان" value={draft.description ?? ''} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} onKeyDown={handleDraftEnter} />
              <select
                className="border rounded px-2 py-2 text-sm bg-white text-slate-700 border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:focus:ring-offset-slate-800"
                value={(draft.reason as ExpenseCategory) ?? ''}
                onChange={e => setDraft(d => ({ ...d, reason: e.target.value as ExpenseCategory }))}
                onKeyDown={handleDraftEnter}
              >
                <option value="">سبب الصرف</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <Input type="number" min={0} placeholder="المبلغ" value={(draft.amount ?? '') as any} onChange={e => setDraft(d => ({ ...d, amount: Number(e.target.value) }))} onKeyDown={handleDraftEnter} />
              <Input type="number" min={0} placeholder="مصروفات بنكية" value={(draft.bankFees ?? '') as any} onChange={e => setDraft(d => ({ ...d, bankFees: Number(e.target.value) }))} onKeyDown={handleDraftEnter} />
              <div className="px-2 py-2 text-sm bg-slate-50 border rounded text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700">
                {((Number(draft.amount || 0) + Number(draft.bankFees || 0)) || 0).toLocaleString()}
              </div>
              <Input placeholder="اسم المشتري" value={draft.buyerName ?? ''} onChange={e => setDraft(d => ({ ...d, buyerName: e.target.value }))} onKeyDown={handleDraftEnter} />
              <Input placeholder="ملاحظات" value={draft.notes ?? ''} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} onKeyDown={handleDraftEnter} ref={notesRef} />
            </div>
          </div>

          <div className="flex justify-between">
            <Button onClick={editingLineId ? updateEditedLine : saveDraftLine}>{editingLineId ? 'تحديث السطر' : 'إضافة السطر'}</Button>
            {editingLineId && (
              <Button variant="outline" onClick={cancelEditLine}>إلغاء التعديل</Button>
            )}
            <Button variant="secondary" onClick={exportCSV}>
              <Download size={16} className="ml-2" />
              تصدير CSV
            </Button>
          </div>
          {lineAddError && (
            <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {lineAddError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* تمت إزالة نافذة تصفح العهد السابقة حسب التوجيه */}

      {/* Quick Pick overlay */}
      {isQuickPickOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setQuickPickOpen(false)}></div>
          <div className="relative w-[95%] max-w-xl">
            <Card className="shadow-xl">
              <CardHeader className="flex items-center justify-between">
                <CardTitle className="text-base">اختر عهدة</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setQuickPickOpen(false)}>
                  <X size={16} />
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {isBrowseLoading && (
                  <div className="p-2 text-xs text-slate-600 dark:text-slate-300">جاري تحميل العهد...</div>
                )}
                {browseError && (
                  <div className="p-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded flex items-center justify-between gap-2">
                    <span>{browseError}</span>
                    <Button size="xs" variant="outline" onClick={() => fetchSheets()}>إعادة المحاولة</Button>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                  <div className="md:col-span-2">
                    <label className="text-xs text-slate-600 dark:text-slate-300">ابحث بالاسم/الرقم</label>
                    <Input value={quickPickQuery} onChange={e => setQuickPickQuery(e.target.value)} placeholder="مثال: عهدة مشروع الرياض" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-300">أدخل رقم العهدة</label>
                    <div className="flex gap-2">
                      <Input value={quickPickManualId} onChange={e => setQuickPickManualId(e.target.value)} placeholder="رقم" />
                      <Button size="sm" onClick={() => { if (quickPickManualId.trim()) { openSheet(quickPickManualId.trim()); setQuickPickOpen(false); } }}>فتح</Button>
                    </div>
                  </div>
                </div>

                <div className="max-h-64 overflow-auto border rounded">
                  {sheets.length === 0 ? (
                    <div className="p-3 text-xs text-slate-500">لا يوجد عهد سابقة</div>
                  ) : (
                    sheets
                      .filter(s => (quickPickQuery ? (s.custody_number?.toLowerCase().includes(quickPickQuery.toLowerCase())) : true))
                      .map(s => (
                        <div key={s.id} className="grid grid-cols-5 gap-2 py-1 px-2 text-xs items-center hover:bg-slate-50 dark:hover:bg-slate-800">
                          <div className="col-span-2">{s.custody_number || '-'}</div>
                          <div>{Number(s.custody_amount || 0).toLocaleString()}</div>
                          <div><Badge className={`px-2 py-0.5 rounded-full text-[11px] ${s.status === 'CLOSED' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'}`}>{s.status === 'CLOSED' ? 'مغلقة' : 'مفتوحة'}</Badge></div>
                          <div className="text-right"><Button size="xs" onClick={() => { const num = String(s.custody_number ?? String(s.id ?? '').replace(/^CUST-/, '')); openSheet(num); setQuickPickOpen(false); }}>اختيار</Button></div>
                        </div>
                      ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstantExpensesScreen;
